import { logger, shortUuid } from '@tunasong/models'
import type { Game, GameResult } from '@tunasong/schemas'
import { openDatabase } from '@tunasong/indexeddb-storage'
import { useCallback, useEffect, useMemo, useState } from 'react'

/** Hook to use indexeddb to store the game results */
export const useScorePersistIndexedDB = ({ game, nickName }: { game: Game; nickName: string }) => {
  // Save the score as a GameResult object in IndexedDB

  const DB_NAME = `tunasong-scores-${game.gameId}`

  const db = useMemo(() => openDatabase<GameResult>(DB_NAME), [DB_NAME])

  const [userGameResults, setUserGameResults] = useState<GameResult[]>([])

  const saveScore = async (score: number) => {
    if (!db) {
      logger.warn('Database not initialized - cannot save score')
      return
    }
    const result: GameResult = {
      type: 'gameresult',
      score,
      nickName,
      createdAt: new Date().toISOString(),
      // Add other required fields if necessary, or leave as undefined if optional
    }
    const id = shortUuid()
    await db.set(id, result)
    // Add to local game results
    setUserGameResults(prevResults => [...prevResults, result])
  }

  const getScores = useCallback(async () => {
    if (!db) {
      logger.warn('Database not initialized - cannot get scores')
      return []
    }
    const results = await db.values()
    return results
  }, [db])

  useEffect(() => {
    getScores().then(results => {
      setUserGameResults(results)
    })
  }, [db, getScores])

  return { saveScore, userGameResults, gameResults: [] }
}
